import React from 'react';
import { Navbar, Container, Nav, NavDropdown } from 'react-bootstrap';
import { PersonFill, ShieldCheck, Eye } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { useTracks } from '../hooks/useTracks';
import { useAuth } from '../contexts/SecurityContext';
import RoleGuard from './security/RoleGuard';

function Header({ user, onSignOut }) {
  const { tracks, loading: tracksLoading } = useTracks();
  const { signOut } = useAuth();

  // Custom Link component that works with React Router
  const NavLink = ({ to, children }) => (
    <Nav.Link as={Link} to={to}>{children}</Nav.Link>
  );

  // Custom NavDropdown.Item that works with React Router
  const NavDropdownLink = ({ to, children }) => (
    <NavDropdown.Item as={Link} to={to}>{children}</NavDropdown.Item>
  );

  const handleSignOut = async () => {
    await signOut();
    if (onSignOut) {
      onSignOut();
    }
  };

  return (
    <header>
      <Navbar expand="lg">
        <Container>
          <Navbar.Brand as={Link} to="/">Quantum City Challenge</Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              <NavLink to="/page/about">About</NavLink>
              <NavLink to="/page/sponsors">Sponsors</NavLink>
              <NavDropdown title="Tracks" id="track-nav-dropdown">
                <NavDropdownLink to="/page/tracks">Overview</NavDropdownLink>
                <NavDropdown.Divider />
                {tracksLoading ? (
                  <NavDropdown.Item disabled>Loading tracks...</NavDropdown.Item>
                ) : (
                  tracks.map(track => (
                    <NavDropdownLink 
                      key={track.id} 
                      to={`/page/tracks/${track.slug}`}
                    >
                      {track.name}
                    </NavDropdownLink>
                  ))
                )}
                <NavDropdown.Divider />
                <NavDropdownLink to="/user/submit">Submit a Solution</NavDropdownLink>
                <NavDropdownLink to="/user/ask">Ask a Question</NavDropdownLink>
                <NavDropdown.Divider />
                <NavDropdownLink to="/tracks/answers">All Answers</NavDropdownLink>
              </NavDropdown>
            </Nav>
            <Nav>
              <RoleGuard requireAuth fallback={<NavLink to="/user">Login</NavLink>}>
                {/* Review Menu - for reviewers and admins */}
                <RoleGuard roles={['reviewer', 'admin']}>
                  <NavLink to="/review" className="me-2">
                    <Eye className="me-1" />Review
                  </NavLink>
                </RoleGuard>
                
                {/* Admin Menu - only for admins */}
                <RoleGuard role="admin">
                  <NavDropdown 
                    title={<span><ShieldCheck className="me-1" />Admin</span>} 
                    id="admin-nav-dropdown"
                    className="me-2"
                  >
                    <NavDropdownLink to="/admin">
                      <ShieldCheck className="me-2" />Dashboard
                    </NavDropdownLink>
                    <NavDropdown.Divider />
                    <NavDropdownLink to="/admin/stats">Platform Statistics</NavDropdownLink>
                    <NavDropdownLink to="/admin/users">User Management</NavDropdownLink>
                    <NavDropdownLink to="/admin/content">Content Management</NavDropdownLink>
                    <NavDropdown.Divider />
                    <NavDropdownLink to="/admin/tracks">Challenge Configuration</NavDropdownLink>
                    <NavDropdownLink to="/admin/review">Assign Reviews</NavDropdownLink>
                    <NavDropdownLink to="/review/summary">Grading Results</NavDropdownLink>
                    <NavDropdownLink to="/admin/questions">Manage Questions</NavDropdownLink>
                  </NavDropdown>
                </RoleGuard>
                
                {/* User Menu - for authenticated users */}
                <NavDropdown 
                  title={
                    <span>
                      <PersonFill className="me-1" />
                      {user?.attributes?.given_name || user?.attributes?.name || user?.username || 'User'}
                    </span>
                  } 
                  id="user-nav-dropdown"
                >
                  {/* User Info Header - Non-clickable */}
                  <div className="px-3 py-2 text-muted small border-bottom">
                    <div className="text-truncate" style={{maxWidth: '200px'}}>
                      {user?.attributes?.email || 'No email'}
                    </div>
                  </div>
                  
                  <NavDropdownLink to="/user">Account</NavDropdownLink>
                  <NavDropdownLink to="/user/submit">Submit Solution</NavDropdownLink>
                  <NavDropdownLink to="/user/submissions">My Submissions</NavDropdownLink>
                  <NavDropdown.Divider />
                  <NavDropdownLink to="/user/ask">Ask Question</NavDropdownLink>
                  <NavDropdownLink to="/user/questions">My Questions</NavDropdownLink>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleSignOut}>Logout</NavDropdown.Item>
                </NavDropdown>
              </RoleGuard>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </header>
  );
}

export default Header;
